import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Конфигурация Sentry приходит с бекенда (GET /api/auth/v3/client-config), а не
 * зашивается в бандл при сборке: в контурах, где Sentry недоступен, бек не
 * отдаёт dsn, SDK не инициализируется и браузер не сыпет ошибками сети в консоль.
 * Если бек отдал tunnel — события уходят на свой origin, а до Sentry их
 * доставляет бекенд (он же обеспечивает доверие к внутреннему сертификату).
 */
type SentryClientConfig = {
  dsn: string;
  environment: string;
  release: string;
  tunnel: string | null;
  traces_sample_rate: number;
  replays_session_sample_rate: number;
};

type ClientConfig = {
  sentry: SentryClientConfig | null;
};

const CLIENT_CONFIG_URL = '/api/auth/v3/client-config';
const CLIENT_CONFIG_TIMEOUT = 3000;

/** DSN, с которым реально проинициализирован SDK. Пустая строка — Sentry выключен */
export let sentryDsn = '';

let initStarted = false;

const fetchClientConfig = async (): Promise<ClientConfig | null> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CLIENT_CONFIG_TIMEOUT);

  try {
    const response = await fetch(CLIENT_CONFIG_URL, {
      signal: controller.signal,
      credentials: 'same-origin',
    });

    return response.ok ? await response.json() : null;
  } catch {
    // Бек недоступен — работаем без Sentry, это не повод ронять загрузку
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Дефолтный BrowserSession шлёт envelope на любой pushState/replaceState,
 * включая смену query (useURLState). Новую сессию открываем только при
 * смене pathname — query/hash это состояние UI, не новый просмотр страницы.
 */
export const PATHNAME_BROWSER_SESSION_INTEGRATION = 'PathnameBrowserSession';

export const pathnameBrowserSessionIntegration = () => ({
  name: PATHNAME_BROWSER_SESSION_INTEGRATION,
  setupOnce() {
    Sentry.startSession({ ignoreDuration: true });
    Sentry.captureSession();

    let lastPathname = window.location.pathname;

    const maybeRotateSession = () => {
      const pathname = window.location.pathname;
      if (pathname === lastPathname) {
        return;
      }
      lastPathname = pathname;
      Sentry.startSession({ ignoreDuration: true });
      Sentry.captureSession();
    };

    const patch = (method: 'pushState' | 'replaceState') => {
      const original = window.history[method].bind(window.history);
      window.history[method] = (...args: Parameters<History['pushState']>) => {
        const result = original(...args);
        maybeRotateSession();
        return result;
      };
    };

    patch('pushState');
    patch('replaceState');
    window.addEventListener('popstate', maybeRotateSession);
  },
});

export const initSentry = async (): Promise<void> => {
  if (initStarted || Sentry.isInitialized()) {
    return;
  }
  initStarted = true;

  const config = await fetchClientConfig();
  const sentry = config?.sentry;

  if (!sentry?.dsn) {
    return;
  }

  sentryDsn = sentry.dsn;

  Sentry.init({
    dsn: sentry.dsn,
    // undefined вместо null: SDK ждёт отсутствия опции, а не пустого значения
    tunnel: sentry.tunnel ?? undefined,
    environment: sentry.environment || window.location.hostname,
    release: sentry.release,
    integrations: (defaults) => [
      ...defaults.filter(
        (integration) => integration.name !== 'BrowserSession',
      ),
      Sentry.extraErrorDataIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      pathnameBrowserSessionIntegration(),
    ],
    tracesSampleRate: sentry.traces_sample_rate,
    replaysSessionSampleRate: sentry.replays_session_sample_rate,
    replaysOnErrorSampleRate: 1.0,
  });
};